import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Auth helpers
export const signUp = async (email: string, password: string, username: string, fullName: string, ageGroup: string) => {
  const { data: authData, error: authError } = await supabase.auth.signUp({
    email,
    password,
  });

  if (authError) throw authError;

  if (authData.user) {
    const { error: profileError } = await supabase
      .from('users')
      .insert({
        id: authData.user.id,
        email,
        username,
        full_name: fullName,
        age_group: ageGroup,
      });

    if (profileError) throw profileError;
  }

  return authData;
};

export const signIn = async (email: string, password: string) => {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });

  if (error) throw error;
  return data;
};

export const signOut = async () => {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
};

export const getCurrentUser = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  return user;
};

// Video helpers
export const uploadVideo = async (file: File, userId: string) => {
  const fileExt = file.name.split('.').pop();
  const fileName = `${userId}-${Date.now()}.${fileExt}`;
  const filePath = `${userId}/${fileName}`;

  const { error: uploadError } = await supabase.storage
    .from('videos')
    .upload(filePath, file);

  if (uploadError) throw uploadError;

  const { data: { publicUrl } } = supabase.storage
    .from('videos')
    .getPublicUrl(filePath);

  return publicUrl;
};

export const uploadThumbnail = async (file: File, userId: string) => {
  const fileExt = file.name.split('.').pop();
  const fileName = `${userId}-${Date.now()}.${fileExt}`;
  const filePath = `${userId}/${fileName}`;

  const { error: uploadError } = await supabase.storage
    .from('thumbnails')
    .upload(filePath, file);

  if (uploadError) throw uploadError;

  const { data: { publicUrl } } = supabase.storage
    .from('thumbnails')
    .getPublicUrl(filePath);

  return publicUrl;
};

export const createVideo = async (videoData: {
  user_id: string;
  title: string;
  description: string;
  video_url: string;
  thumbnail_url?: string;
  category: string;
  duration?: number;
}) => {
  const { data, error } = await supabase
    .from('videos')
    .insert(videoData)
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const getVideos = async (category?: string, limit = 20) => {
  let query = supabase
    .from('videos')
    .select(`
      *,
      users (
        id,
        username,
        full_name,
        avatar_url,
        verified
      )
    `)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (category) {
    query = query.eq('category', category);
  }

  const { data, error } = await query;

  if (error) throw error;
  return data;
};

export const getUserVideos = async (userId: string) => {
  const { data, error } = await supabase
    .from('videos')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data;
};

// Like helpers
export const likeVideo = async (userId: string, videoId: string) => {
  const { error } = await supabase
    .from('likes')
    .insert({ user_id: userId, video_id: videoId });

  if (error) throw error;

  await supabase.rpc('increment_likes', { video_id: videoId });
};

export const unlikeVideo = async (userId: string, videoId: string) => {
  const { error } = await supabase
    .from('likes')
    .delete()
    .eq('user_id', userId)
    .eq('video_id', videoId);

  if (error) throw error;

  await supabase.rpc('decrement_likes', { video_id: videoId });
};

export const checkIfLiked = async (userId: string, videoId: string) => {
  const { data, error } = await supabase
    .from('likes')
    .select('id')
    .eq('user_id', userId)
    .eq('video_id', videoId)
    .single();

  if (error && error.code !== 'PGRST116') throw error;
  return !!data;
};

// Comment helpers
export const createComment = async (userId: string, videoId: string, content: string, parentId?: string) => {
  const { data, error } = await supabase
    .from('comments')
    .insert({
      user_id: userId,
      video_id: videoId,
      content,
      parent_id: parentId,
    })
    .select()
    .single();

  if (error) throw error;

  await supabase.rpc('increment_comments', { video_id: videoId });

  return data;
};

export const getComments = async (videoId: string) => {
  const { data, error } = await supabase
    .from('comments')
    .select(`
      *,
      users (
        id,
        username,
        full_name,
        avatar_url,
        verified
      )
    `)
    .eq('video_id', videoId)
    .is('parent_id', null)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data;
};

// Follow helpers
export const followUser = async (followerId: string, followingId: string) => {
  const { error } = await supabase
    .from('follows')
    .insert({ follower_id: followerId, following_id: followingId });

  if (error) throw error;

  await supabase.rpc('increment_followers', { user_id: followingId });
  await supabase.rpc('increment_following', { user_id: followerId });
};

export const unfollowUser = async (followerId: string, followingId: string) => {
  const { error } = await supabase
    .from('follows')
    .delete()
    .eq('follower_id', followerId)
    .eq('following_id', followingId);

  if (error) throw error;

  await supabase.rpc('decrement_followers', { user_id: followingId });
  await supabase.rpc('decrement_following', { user_id: followerId });
};

export const checkIfFollowing = async (followerId: string, followingId: string) => {
  const { data, error } = await supabase
    .from('follows')
    .select('id')
    .eq('follower_id', followerId)
    .eq('following_id', followingId)
    .single();

  if (error && error.code !== 'PGRST116') throw error;
  return !!data;
};

// User helpers
export const getUserProfile = async (userId: string) => {
  const { data, error } = await supabase
    .from('users')
    .select('*')
    .eq('id', userId)
    .single();

  if (error) throw error;
  return data;
};

export const updateUserProfile = async (userId: string, updates: {
  full_name?: string;
  bio?: string;
  avatar_url?: string;
}) => {
  const { data, error } = await supabase
    .from('users')
    .update(updates)
    .eq('id', userId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

// Project Video helpers
export const uploadProjectVideo = async (file: File, projectName: string) => {
  const fileExt = file.name.split('.').pop();
  const fileName = `projects/${projectName}-${Date.now()}.${fileExt}`;
  const filePath = fileName;

  const { error: uploadError } = await supabase.storage
    .from('videos')
    .upload(filePath, file, {
      cacheControl: '3600',
      upsert: false
    });

  if (uploadError) {
    console.error('Upload error:', uploadError);
    throw uploadError;
  }

  const { data: { publicUrl } } = supabase.storage
    .from('videos')
    .getPublicUrl(filePath);

  return publicUrl;
};

export const saveProjectVideo = async (videoData: {
  project_name: string;
  video_url: string;
  thumbnail_url?: string;
  description?: string;
}) => {
  const { data, error } = await supabase
    .from('project_videos')
    .insert(videoData)
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const getProjectVideos = async () => {
  const { data, error } = await supabase
    .from('project_videos')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data;
};

export const getProjectVideoByName = async (projectName: string) => {
  const { data, error } = await supabase
    .from('project_videos')
    .select('*')
    .eq('project_name', projectName)
    .order('created_at', { ascending: false })
    .limit(1)
    .single();

  if (error && error.code !== 'PGRST116') throw error;
  return data;
};
